import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { forEach } from 'jszip';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ClassesService } from 'src/app/services/classes.service';
import { GeneralCalculationsService } from 'src/app/services/general-calculations.service';
import { PilotSchoolsService } from 'src/app/services/pilot-schools.service';

@Component({
  selector: 'app-banner-feedback',
  standalone: true,
  imports: [TranslateModule, CommonModule],
  templateUrl: './banner-feedback.component.html',
  styleUrl: './banner-feedback.component.scss',
  animations: [
    trigger('slideInOut', [
      state("collapsed", style({
        overflow: 'hidden',
        height: '40px'
      })),
      state("expanded", style({
        opacity: '1',
        overflow: 'hidden',
        height: '*'
      })),
      transition('collapsed <=> expanded', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})

  
export class BannerFeedbackComponent {

  classesWithBaselineStarted: any[] = [];
  nextActiveQuestionnairesTwoWeeks: any[] = [];
  currentActiveQuestionnaires: any[] = [];
  currentActiveQuestionnaire: Date;
  nextActiveQuestionnaireInTwoWeeks: Date;
  timeLeftNextQuestionnaire: { days: number; hours: number; minutes: number; seconds: number } | null = null;
  timeLefActiveQuestionnaire: { days: number; hours: number; minutes: number; seconds: number } | null = null;
  timer: any;
  dataLoaded: boolean = false;
  showNextQuestionnaireBanner = false;
  showActiveQuestionnaireBanner = false;
  role: string = "";
  nextQuestionnairesStatus: string = "collapsed";
  activeQuestionnairesStatus: string = "collapsed";

  /** Data subscriptions */
  private joinClassSubscription: Subscription = new Subscription();
  private leaveClassSubscription: Subscription = new Subscription();

  constructor(private classesService: ClassesService, private pilotSchoolsService: PilotSchoolsService, private generalCalculationsService: GeneralCalculationsService,
    private authService: AuthService, private router: Router, private cdRef: ChangeDetectorRef, private ngZone: NgZone) { }


  ngOnInit() {

    if (this.authService.role == "TEACHER_ROLE") {
      this.role = "educator";
      this.calculateNextActiveQuestionnaire();
    } else if (this.authService.role == "ADMIN_ROLE") {
      this.role = "admin";
      this.calculateNextActiveQuestionnaire();
    } else {
      this.role = "viewer";
    }


    this.ngZone.runOutsideAngular(() => {
      this.timer = setInterval(() => {
        this.updateCountdown();
        this.updateCountdownActiveQuestionnaire();
      }, 1000);
    });

    this.joinClassSubscription = this.classesService.userToClass$.subscribe(() => {

      this.calculateNextActiveQuestionnaire();

    })

    this.leaveClassSubscription = this.classesService.removeUserToClass$.subscribe(() => {

      this.calculateNextActiveQuestionnaire();

    })


  }

  async calculateNextActiveQuestionnaire() {

    this.showNextQuestionnaireBanner = false;
    this.showActiveQuestionnaireBanner = false;
    this.dataLoaded = false;

    let classes: any = [];

    if (this.authService.role == "TEACHER_ROLE") {
      classes = JSON.parse(JSON.stringify(await this.classesService.getClassesByEducator(this.authService.userProfileData.id).toPromise()));
    } else {
      classes = JSON.parse(JSON.stringify(await this.classesService.getAllClasses().toPromise()));
    }

    let classesData = JSON.parse(JSON.stringify(classes));
    if (classesData.length > 0) {
      this.classesWithBaselineStarted = classesData.filter((classData: any) => classData.baselineQuestionnaireStart);
    }

    this.classesWithBaselineStarted.forEach((classData: any, index: number) => {

      let dates = this.generalCalculationsService.calculateQuestionnaireDates(classData.baselineQuestionnaireStart);

      let periodAndDate = this.questionnaireInProgress(dates);
      if (periodAndDate.period == 'isBaseline' || periodAndDate.period == 'isMid' || periodAndDate.period == 'isFinal') {
        this.classesWithBaselineStarted[index].endActiveQuestionnaireDate = periodAndDate.finalDate;
        this.classesWithBaselineStarted[index].activeQuestionnaire = true;
      } else {
        this.classesWithBaselineStarted[index].activeQuestionnaire = false;
      }

      this.classesWithBaselineStarted[index].startBaselineQuestionnaire = dates.startBaselineQuestionnaire;
      this.classesWithBaselineStarted[index].endBaselineQuestionnaire = dates.endBaselineQuestionnaire;
      this.classesWithBaselineStarted[index].startMidQuestionnaire = dates.startMidQuestionnaire;
      this.classesWithBaselineStarted[index].endMidQuestionnaire = dates.endMidQuestionnaire;
      this.classesWithBaselineStarted[index].startFinalQuestionnaire = dates.startFinalQuestionnaire;
      this.classesWithBaselineStarted[index].endFinalQuestionnaire = dates.endFinalQuestionnaire;

      //Calculate for each class which is the next active questionnaire

      let currentDate = new Date();

      //Period V1

      if (currentDate < new Date(this.classesWithBaselineStarted[index].startMidQuestionnaire)) {
        this.classesWithBaselineStarted[index].nextActiveQuestionnaire = JSON.parse(JSON.stringify(this.classesWithBaselineStarted[index].startMidQuestionnaire));
      }

      //Period V6
      else if (currentDate >= new Date(this.classesWithBaselineStarted[index].startMidQuestionnaire) && currentDate < new Date(this.classesWithBaselineStarted[index].startFinalQuestionnaire)) {
        this.classesWithBaselineStarted[index].nextActiveQuestionnaire = JSON.parse(JSON.stringify(this.classesWithBaselineStarted[index].startFinalQuestionnaire));
      }

      //Period V18
      else if (currentDate >= new Date(this.classesWithBaselineStarted[index].startFinalQuestionnaire)) {
        this.classesWithBaselineStarted[index].nextActiveQuestionnaire = "-";
      }

    });

    let nextActiveQuestionnaires = this.classesWithBaselineStarted.map((classData: any) => { return { id: classData.id, name: classData.course + "º" + classData.name, schoolId: classData.schoolId, schoolName: "", date: new Date(classData.nextActiveQuestionnaire) } });

    let currentActiveQuestionnaires = this.classesWithBaselineStarted
      .filter((c: any) => c.activeQuestionnaire)
      .map((c: any) => ({
        id: c.id,
        name: `${c.course}º${c.name}`,
        schoolId: c.schoolId,
        schoolName: "",
        date: new Date(c.endActiveQuestionnaireDate)
      }));

    let nextActiveQuestionnairesTwoWeeks = nextActiveQuestionnaires.filter((element: any) => {
      let currentDate = new Date();
      let twoWeeks = 1000 * 60 * 60 * 24 * 14; // 14 days in milliseconds
      if (element.date.getTime() - currentDate.getTime() <= twoWeeks) {
        return element;
      }
    });

    if (nextActiveQuestionnairesTwoWeeks.length > 0 || currentActiveQuestionnaires.length > 0) {

      //If the user role is admin, the school name is needed
      if (this.authService.role != "TEACHER_ROLE") {

        let schoolsData = JSON.parse(JSON.stringify(await this.pilotSchoolsService.getAllSchoolsDataObservable().toPromise()));

        currentActiveQuestionnaires.forEach((element, index) => {

          schoolsData.forEach((school: any) => {
            if (school.id === element.schoolId) {
              element.schoolName = school.name;
            }
          })
        });

        nextActiveQuestionnairesTwoWeeks.forEach((element, index) => {

          schoolsData.forEach((school: any) => {
            if (school.id === element.schoolId) {
              element.schoolName = school.name;
            }
          })
        });

      }

      //Order by date
      this.currentActiveQuestionnaires = currentActiveQuestionnaires.sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
      this.nextActiveQuestionnairesTwoWeeks = nextActiveQuestionnairesTwoWeeks.sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

      //Check if there are schools repeated
      if (this.role == 'admin') {

        let arrayOfSchoolIdsNextActiveQuestionnaire: any = [];
        let arrayOfSchoolIdsCurrentActiveQuestionnaires: any = [];

        let processedNextActiveQuestionnairesTwoWeeks: any = [];
        let processedCurrentActiveQuestionnaires: any = [];

        this.nextActiveQuestionnairesTwoWeeks.forEach((item, index) => {

          if (arrayOfSchoolIdsNextActiveQuestionnaire.indexOf(item.schoolId) == -1) {
            arrayOfSchoolIdsNextActiveQuestionnaire.push(item.schoolId);
            processedNextActiveQuestionnairesTwoWeeks.push(item);
          }

        })

        this.nextActiveQuestionnairesTwoWeeks = JSON.parse(JSON.stringify(processedNextActiveQuestionnairesTwoWeeks));

        this.currentActiveQuestionnaires.forEach((item) => {

          if (arrayOfSchoolIdsCurrentActiveQuestionnaires.indexOf(item.schoolId) == -1) {
            arrayOfSchoolIdsCurrentActiveQuestionnaires.push(item.schoolId);
            processedCurrentActiveQuestionnaires.push(item);
          }

        })

        this.currentActiveQuestionnaires = JSON.parse(JSON.stringify(processedCurrentActiveQuestionnaires));

      }

      if(this.nextActiveQuestionnairesTwoWeeks.length!=0){
         this.nextActiveQuestionnaireInTwoWeeks = new Date(JSON.parse(JSON.stringify(this.nextActiveQuestionnairesTwoWeeks[0].date)));
      }
       if(this.currentActiveQuestionnaires.length!=0){
        this.currentActiveQuestionnaire = new Date(JSON.parse(JSON.stringify(this.currentActiveQuestionnaires[0].date)));
      }


      if (this.nextActiveQuestionnaireInTwoWeeks) {

        this.showNextQuestionnaireBanner = true;

      }

      if(this.currentActiveQuestionnaire){
        this.showActiveQuestionnaireBanner = true;
      }

    }

    this.dataLoaded = true;

  }


  /**
   * Method to update the countdown timer
   */
  updateCountdown() {
    const now = new Date().getTime();
    if (this.nextActiveQuestionnaireInTwoWeeks) {
      const diff = this.nextActiveQuestionnaireInTwoWeeks.getTime() - now;

      if (diff <= 0) {
        this.timeLeftNextQuestionnaire = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      } else {
        this.timeLeftNextQuestionnaire = {
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        };
      }

      // Manually trigger change detection
      this.cdRef.detectChanges();
    }

  }

    /**
   * Method to update the countdown timer
   */
  updateCountdownActiveQuestionnaire() {
    const now = new Date().getTime();
    if (this.currentActiveQuestionnaire) {
      const diff = this.currentActiveQuestionnaire.getTime() - now;

      if (diff <= 0) {
        this.timeLefActiveQuestionnaire = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      } else {
        this.timeLefActiveQuestionnaire = {
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        };
      }

      // Manually trigger change detection
      this.cdRef.detectChanges();
    }

  }


  navigateToSection(section: string) {
    this.router.navigate([section]);
  }




  /**
   * Method to open a link in a new tab
   * @param link 
   * @param $event 
   */
  openLink(link: string, $event: MouseEvent) {

    $event.stopPropagation();
    window.open(link, '_blank');
  }

  questionnaireInProgress(questionnaireDate: any) {
    let object = {
      "period": "",
      "finalDate": new Date()
    }
    let today = new Date();
    if (today >= new Date(questionnaireDate.startBaselineQuestionnaire) && today <= new Date(questionnaireDate.endBaselineQuestionnaire)) {
      object.period = "isBaseline";
      object.finalDate = new Date(questionnaireDate.endBaselineQuestionnaire);
    } else if (today >= new Date(questionnaireDate.startMidQuestionnaire) && today <= new Date(questionnaireDate.endMidQuestionnaire)) {
      object.period = "isMid";
      object.finalDate = new Date(questionnaireDate.endMidQuestionnaire);
    } else if (today >= new Date(questionnaireDate.startFinalQuestionnaire) && today <= new Date(questionnaireDate.endFinalQuestionnaire)) {
      object.period = "isFinal";
      object.finalDate = new Date(questionnaireDate.endFinalQuestionnaire);
    } else {
      object.period = "none";
      object.finalDate = new Date(questionnaireDate.endFinalQuestionnaire);
    }

    return object;

  }

  collapse(element: String){

    this.ngZone.runOutsideAngular(() => {
    requestAnimationFrame(() => {
      this.ngZone.run(() => {
        if(element == 'nextQuestionnaires'){
      if(this.nextQuestionnairesStatus == "collapsed"){
        this.nextQuestionnairesStatus = "expanded"
      }else{
        this.nextQuestionnairesStatus = "collapsed"
      }
      
    }else{
      if(this.activeQuestionnairesStatus == "collapsed"){
        this.activeQuestionnairesStatus = "expanded"
      }else{
        this.activeQuestionnairesStatus = "collapsed"
      }
    }
        
        
    });
        });
      });
  }

  ngOnDestroy() {
    this.joinClassSubscription.unsubscribe();
    this.leaveClassSubscription.unsubscribe();
  }

}


